import { t } from '../../lib/i18n';

/**
 * Profil fotoğrafı hazırlığı: seçilen görsel ortadan kare kırpılıp 256 px
 * JPEG'e küçültülür (≈20–60 KB). Veritabanına bu küçük data: URL'si yazılıyor;
 * ana süreç türünü ve boyutunu ayrıca doğruluyor.
 */

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_INPUT_BYTES = 5 * 1024 * 1024;
const SIZE = 256;

export const AVATAR_ACCEPT = ACCEPTED_TYPES.join(',');

/** Kareye kırpıp küçülten ortak adım: dosya ve kamera karesi aynı yoldan geçiyor. */
function toSquareDataUrl(source: CanvasImageSource, width: number, height: number): string {
  const side = Math.min(width, height);
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const context = canvas.getContext('2d');
  if (!context) throw new Error(t('Görsel işlenemedi.', 'Could not process the image.'));

  // JPEG saydamlık taşımıyor; saydam PNG'ler siyah değil beyaz zeminde kalsın.
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, SIZE, SIZE);
  context.imageSmoothingQuality = 'high';
  context.drawImage(source, (width - side) / 2, (height - side) / 2, side, side, 0, 0, SIZE, SIZE);
  return canvas.toDataURL('image/jpeg', 0.88);
}

/** Kameradan alınan canlı kareyi profil fotoğrafına çevirir. */
export function captureAvatarFrame(video: HTMLVideoElement): string {
  const width = video.videoWidth;
  const height = video.videoHeight;
  if (!width || !height) throw new Error(t('Kamera görüntüsü hazır değil.', 'The camera image is not ready yet.'));
  return toSquareDataUrl(video, width, height);
}

export async function prepareAvatar(file: File): Promise<string> {
  if (!ACCEPTED_TYPES.includes(file.type)) {
    throw new Error(t('PNG, JPG ya da WebP bir görsel seçin.', 'Choose a PNG, JPG or WebP image.'));
  }
  if (file.size > MAX_INPUT_BYTES) {
    throw new Error(t('Görsel en fazla 5 MB olabilir.', 'The image must be 5 MB or smaller.'));
  }

  let bitmap: ImageBitmap;
  try {
    // Chromium EXIF yönünü kendiliğinden uyguluyor; telefon fotoğrafları yan dönmüyor.
    bitmap = await createImageBitmap(file);
  } catch {
    throw new Error(t('Görsel okunamadı.', 'Could not read the image.'));
  }

  try {
    return toSquareDataUrl(bitmap, bitmap.width, bitmap.height);
  } finally {
    bitmap.close();
  }
}
